import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { message, notification } from 'antd';
import ConfigUtility from 'Core/ConfigUtility';
import React, { Fragment } from 'react';
import NotificationUtility from 'Core/NotificationUtility';
import TokenUtility from 'Core/TokenUtility';
import { uniqueId } from 'lodash';
import Utility from 'Core/Utility';
import LoaderUtility from 'Core/LoaderUtility';

const Config = ConfigUtility.getSync()!;

export interface ErrorDetails {
  statusCode?: number;
  message?: string;
  eventMarker?: string;
}

export interface ExtendedAxios extends AxiosInstance {
  ShowLoader: (config: any) => void;
  HideLoader: (config: any) => void;
  handleSecurityError?: boolean;
  baseUrlConfigName?: string;
  showLoading?: boolean;
  handleGeneralError?: boolean;
  disableToken?: boolean;
  GetErrorDetails(error: any);
}
// tslint:disable-next-line:prefer-object-spread
const getSecuredAxiosInstance: () => ExtendedAxios = () => {
  const SecuredAxios: ExtendedAxios = Object.assign(
    {
      disableToken: false,
      handleSecurityError: true,
      ShowLoader: () => { },
      HideLoader: () => { },
      GetErrorDetails: () => { },
    },
    axios.create({})
  );

  SecuredAxios.interceptors.request.use(
    async config => {
      // turning on loader
      const appConfig = await ConfigUtility.get();
      config.baseURL = appConfig[SecuredAxios.baseUrlConfigName!];
      config.timeout = (await ConfigUtility.get()).DefaultRequestTimeout || 60000;
      SecuredAxios.ShowLoader(config);

      // Add Correlation ID header
      config.headers[appConfig.CorrelationIdHeader] = Utility.GetOrGenerateCorrleationId();
      config.headers[appConfig["SessionIdHeader"] ?? "SessionId"] = TokenUtility.GetSessionId();

      return config;
    },
    error => {
      // Do something with error
      return Promise.reject(error);
    }
  );

  SecuredAxios.interceptors.response.use(
    response => {
      SecuredAxios.HideLoader(response.config);
      return response;
    },
    (error: { config: AxiosRequestConfig; response: AxiosResponse }) => {
      HideAllLoaders();
      HandleSecurityErrors(error);
      HandleErrors(error);
      return Promise.reject(error);
    }
  );

  function HideAllLoaders() {
    if (SecuredAxios.showLoading) {
      message.destroy();
    }
  }

  function HandleSecurityErrors(error: {
    config: AxiosRequestConfig;
    response: AxiosResponse<any>;
  }) {
    if (SecuredAxios.handleSecurityError) {
      const errorDetails = SecuredAxios.GetErrorDetails(error);
      if (errorDetails.statusCode === 403) {
        NotificationUtility.error(
          'You do not have permission to access that operation. You can try login in with different credentials that do.',
          '',
          0,
          true
        );
      }
      if (errorDetails.statusCode === 401) {
        NotificationUtility.error(
          'You were inactive for a long time and need to login again. Please login to continue',
          '',
          0,
          true
        );
      }
    }
  }

  function HandleErrors(error: { config: AxiosRequestConfig; response: AxiosResponse<any> }) {
    if (SecuredAxios.handleGeneralError) {
      const errorDetails = SecuredAxios.GetErrorDetails(error);

      switch (errorDetails.statusCode) {
        case 400:
          NotificationUtility.error(errorDetails.message, '', 0, true);

          break;

        case 800:
          NotificationUtility.error(
            'Had a problem making that last request, please ensure you are connected to the internet.',
            '',
            0,
            true
          );
          break;

        case 403:
        case 401:
          // It is a security error and will be handled somewhere else
          break;

        default:
          NotificationUtility.error(
            'Oops...Something Went Wrong',
            <>
              <p>
                Somthing happend on that last request. Please contact administrator with this error
                code:
              </p>
              <p>{errorDetails.eventMarker}</p>
            </>,
            0,
            true
          );
          break;
      }
    }
  }

  SecuredAxios.GetErrorDetails = function (error: any) {
    return (error.response || { data: { statusCode: 800, eventMarker: '1111-1111-1111-1111' } })
      .data as ErrorDetails; // here we handle the senario if the issue is client side and we don't get back the response object
  };

  SecuredAxios.HideLoader = function (config: any) {
    LoaderUtility.hide();
  };

  SecuredAxios.ShowLoader = function ShowLoader(config: any) {
    LoaderUtility.show();
  };

  return SecuredAxios;
};

export default getSecuredAxiosInstance;
